import { MAX_CHAT_CHARS, type RoomChatEntry } from "@golazo/shared";
import type { OnlineClient } from "./client.js";

/**
 * El chat de la sala en la que estas, compartido por la pantalla de la sala y
 * el partido.
 *
 * Vive fuera de las dos a proposito: si cada una escuchara el socket por su
 * cuenta, lo que se dijo mientras jugabas no estaria al volver a la sala, y lo
 * que se dijo en la sala no se veria al empezar el partido. Es la misma
 * conversacion, asi que es un solo almacen.
 */
export interface RoomChat {
  /** Sala cuyo chat se esta guardando, o null. */
  readonly roomId: string | null;
  entries(): readonly RoomChatEntry[];
  /** `added` = las lineas nuevas de este aviso (vacio si se ha cambiado de sala). */
  subscribe(fn: (entries: readonly RoomChatEntry[], added: readonly RoomChatEntry[]) => void): () => void;
  /** Escribir en la sala actual. No hace nada si no hay sala o no hay texto. */
  send(text: string): void;
  /** Se ha salido de la sala: se olvida su conversacion. */
  clear(): void;
  dispose(): void;
}

/** Lineas que se guardan. El servidor manda como mucho 40 al entrar. */
const KEEP = 80;

export function createRoomChat(client: OnlineClient): RoomChat {
  let roomId: string | null = null;
  let lines: RoomChatEntry[] = [];
  const listeners = new Set<(entries: readonly RoomChatEntry[], added: readonly RoomChatEntry[]) => void>();

  const emit = (added: readonly RoomChatEntry[]): void => {
    for (const fn of listeners) fn(lines, added);
  };

  const unsub = client.onMessage((msg) => {
    if (msg.type === "room:chat") {
      if (msg.roomId !== roomId) {
        // Otra sala: su conversacion sustituye a la anterior entera.
        roomId = msg.roomId;
        lines = [];
      }
      const known = new Set(lines.map((l) => l.id));
      const added = msg.entries.filter((e) => !known.has(e.id));
      if (added.length === 0) return;
      lines = [...lines, ...added].sort((a, b) => a.id - b.id).slice(-KEEP);
      emit(added);
      return;
    }
    if ((msg.type === "room:kicked" && msg.roomId === roomId)) {
      roomId = null;
      lines = [];
      emit([]);
    }
  });

  return {
    get roomId() {
      return roomId;
    },
    entries() {
      return lines;
    },
    subscribe(fn) {
      listeners.add(fn);
      fn(lines, []);
      return () => listeners.delete(fn);
    },
    send(text) {
      const clean = text.replace(/\s+/g, " ").trim().slice(0, MAX_CHAT_CHARS);
      if (!roomId || !clean) return;
      client.send({ type: "room:chat", roomId, text: clean });
    },
    clear() {
      roomId = null;
      lines = [];
      emit([]);
    },
    dispose() {
      unsub();
      listeners.clear();
    },
  };
}
